import {useConfig} from '../contexts/ConfigContext';
import React, { useState, useMemo } from 'react';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { expandWindowToBookableTimes, timeIntervalsOverlap } from '../utils/therapistBookableSlots';
import './BookingWidget.css';

const BookingWidget = ({ 
    therapistName, 
    price, 
    topic_prices,
    therapy_topics,
    availableSlots, 
    onBook, 
    onCancel, 
    loading 
}) => {
    const {config}=useConfig();
    const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [step, setStep] = useState(1); // 1 = выбор времени, 2 = форма данных
    
    // Проверяем наличие расписания
    const hasSchedule = availableSlots && (
        (availableSlots.recurring_schedule && availableSlots.recurring_schedule.length > 0) ||
        (availableSlots.temporary_schedule && availableSlots.temporary_schedule.length > 0)
    );
    
    const [selectedFormat, setSelectedFormat] = useState('online'); // online или offline
    
    const [selectedTopic, setSelectedTopic] = useState('');
    const [formData, setFormData] = useState({
        client_name: '',
        client_email: '',
        client_phone: '',
        preferred_contact: '',
        message: ''
    });

    // Вычисляем цену в зависимости от выбранной темы
    const currentPrice = useMemo(() => {
        if (selectedTopic && topic_prices && topic_prices[selectedTopic]) {
            return topic_prices[selectedTopic];
        }
        return price || null;
    }, [selectedTopic, topic_prices, price]);

    // Дни текущей недели
    const weekDays = useMemo(() => {
        return Array.from({ length: 7 }).map((_, i) => addDays(currentWeekStart, i));
    }, [currentWeekStart]);

    // Получаем доступные слоты для выбранной даты и формата
    const availableSlotsForDate = useMemo(() => {
        if (!selectedDate || !availableSlots) return [];

        const dayOfWeek = selectedDate.getDay();
        const dateStr = format(selectedDate, 'yyyy-MM-dd');

        // Постоянные окна для выбранного формата
        const recurringSlots = availableSlots.recurring_schedule?.filter(
            slot => slot.day_of_week === dayOfWeek && !(slot.excluded_dates || []).includes(dateStr) && (slot.format || 'online') === selectedFormat
        ) || [];

        // Временные окна для выбранного формата
        const temporarySlots = availableSlots.temporary_schedule?.filter(
            slot => slot.specific_date?.split('T')[0] === dateStr && (slot.format || 'online') === selectedFormat
        ) || [];

        const allRawSlots = [...recurringSlots, ...temporarySlots];

        const expandedByKey = new Map();
        for (const slot of allRawSlots) {
            const fmt = slot.format || 'online';
            for (const piece of expandWindowToBookableTimes(slot.start_time, slot.end_time)) {
                const key = `${fmt}|${piece.start_time}|${piece.end_time}`;
                if (!expandedByKey.has(key)) {
                    expandedByKey.set(key, { ...piece, format: fmt });
                }
            }
        }
        const expandedSlots = Array.from(expandedByKey.values());

        const bookedSlots = availableSlots.booked_slots?.filter(
            (b) =>
                b.booking_date?.split('T')[0] === dateStr &&
                (b.format || 'online') === selectedFormat
        ) || [];

        return expandedSlots
            .filter((slot) => slot.format === selectedFormat)
            .filter(
                (slot) =>
                    !bookedSlots.some((booked) =>
                        timeIntervalsOverlap(
                            booked.start_time,
                            booked.end_time,
                            slot.start_time,
                            slot.end_time
                        )
                    )
            )
            .sort((a, b) => a.start_time.localeCompare(b.start_time));
    }, [selectedDate, availableSlots, selectedFormat]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSlotSelect = (slot) => {
        setSelectedSlot(slot);
        setStep(2);
    };

    const handleBackToStep1 = () => {
        setStep(1);
        setSelectedSlot(null);
        setSelectedTopic('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Запрещаем отправку, если нет расписания или не выбран слот
        if (!hasSchedule || !selectedSlot) {
            return;
        }

        if (config?.code !== 'mag' && !formData.preferred_contact.trim()) {
            return;
        }
        
        const bookingData = {
            booking_date: format(selectedDate, 'yyyy-MM-dd'),
            start_time: selectedSlot.start_time,
            end_time: selectedSlot.end_time,
            format: selectedFormat,
            topic: selectedTopic || null,
            ...formData,
            preferred_contact: config?.code === 'mag' ? undefined : formData.preferred_contact.trim()
        };
        
        console.log('Отправка записи:', bookingData);
        onBook(bookingData);
    };

    const goToPrevWeek = () => setCurrentWeekStart(prev => addDays(prev, -7));
    const goToNextWeek = () => setCurrentWeekStart(prev => addDays(prev, 7));

    const isPast = (date) => date < new Date().setHours(0, 0, 0, 0);

    return (
        <div className="booking-widget">
            <div className="widget-header">
                <h2>Выберите дату и время</h2>
                <p className="timezone-info">
                    Время указано для Москва (GMT +03:00) – {format(new Date(), 'HH:mm')}
                </p>
            </div>

            {step === 1 && (
                <>
                    <div className="format-selector">
                        <label>Формат консультации:</label>
                        <div className="format-buttons">
                            <button
                                type="button"
                                className={`format-btn ${selectedFormat === 'online' ? 'active' : ''}`}
                                onClick={() => setSelectedFormat('online')}
                            >
                                💻 Онлайн
                            </button>
                            <button
                                type="button"
                                className={`format-btn ${selectedFormat === 'offline' ? 'active' : ''}`}
                                onClick={() => setSelectedFormat('offline')}
                            >
                                🏢 Офлайн
                            </button>
                        </div>
                    </div>

                    <div className="week-navigation">
                        <button className="btn-week-nav" onClick={goToPrevWeek}>
                            <ChevronLeft size={20} />
                        </button>
                        <div className="current-week-label">
                            {format(currentWeekStart, 'd MMMM', { locale: ru })} - {format(addDays(currentWeekStart, 6), 'd MMMM', { locale: ru })}
                        </div>
                        <button className="btn-week-nav" onClick={goToNextWeek}>
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="week-days-grid">
                        {weekDays.map(day => {
                            const isSelected = selectedDate && isSameDay(day, selectedDate);
                            const isDisabled = isPast(day);
                            const isToday = isSameDay(day, new Date());

                            return (
                                <div
                                    key={day.toISOString()}
                                    className={`week-day-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isToday ? 'today' : ''}`}
                                    onClick={() => !isDisabled && setSelectedDate(day)}
                                >
                                    <div className="day-abbr">{format(day, 'EEE', { locale: ru })}</div>
                                    <div className="day-num">{format(day, 'd')}</div>
                                    <div className="day-month">{format(day, 'MMM', { locale: ru })}</div>
                                </div>
                            );
                        })}
                    </div>

                    {selectedDate && (
                        <div className="time-slots-section">
                            <h3>Доступное время</h3>
                            {!hasSchedule && (
                                <div className="no-schedule-warning">
                                    <strong>⚠️ Запись недоступна</strong>
                                    <p>Психолог не указал доступное для записи время. Запись к этому психологу временно недоступна.</p>
                                </div>
                            )}
                            <div className="time-slots-grid">
                                {availableSlotsForDate.length > 0 ? (
                                    availableSlotsForDate.map((slot) => (
                                        <button
                                            key={`${slot.start_time}-${slot.end_time}`}
                                            className="time-slot-button"
                                            onClick={() => handleSlotSelect(slot)}
                                        >
                                            {slot.start_time.slice(0, 5)} – {slot.end_time.slice(0, 5)}
                                        </button>
                                    ))
                                ) : (
                                    <>
                                        {!hasSchedule ? (
                                            <div className="no-schedule-slot">
                                                <p className="no-slots-message">Запись недоступна. У психолога нет расписания.</p>
                                            </div>
                                        ) : (
                                            <p className="no-slots-message">На эту дату нет доступных окон</p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="widget-footer">
                        <button className="btn-cancel" onClick={onCancel}>
                            Отмена
                        </button>
                    </div>
                </>
            )}

            {step === 2 && (
                <div className="booking-form-step">
                    {!hasSchedule || !selectedSlot ? (
                        <div className="no-schedule-warning">
                            <strong>⚠️ Запись недоступна</strong>
                            <p>Психолог не указал доступное для записи время. Запись к этому психологу временно недоступна.</p>
                            <button className="btn-cancel" onClick={handleBackToStep1} style={{ marginTop: '10px' }}>
                                Назад
                            </button>
                        </div>
                    ) : (
                        <>
                    <div className="selected-time-banner">
                        <div>
                            <strong>Выбранное время:</strong>
                            <p className="selected-time-text">
                                {format(selectedDate, 'd MMMM yyyy, EEEE', { locale: ru })}
                                {' '}
                                        {selectedSlot.start_time.slice(0, 5)} – {selectedSlot.end_time.slice(0, 5)}
                            </p>
                        </div>
                        <button className="btn-change" onClick={handleBackToStep1}>
                            Изменить
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="booking-details-form">
                        {/* Выбор темы-запроса */}
                        {therapy_topics && therapy_topics.length > 0 && (
                            <div className="form-group">
                                <label>Тема-запрос (необязательно)</label>
                                <select
                                    value={selectedTopic}
                                    onChange={(e) => setSelectedTopic(e.target.value)}
                                    className="topic-select"
                                >
                                    <option value="">Выберите тему-запрос</option>
                                    {therapy_topics.map(topic => (
                                        <option key={topic} value={topic}>
                                            {topic}
                                        </option>
                                    ))}
                                </select>
                                <small className="form-hint">
                                    Выберите тему-запрос, с которой хотите работать на консультации
                                </small>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Ваше имя *</label>
                            <input
                                type="text"
                                name="client_name"
                                value={formData.client_name}
                                onChange={handleInputChange}
                                required
                                placeholder="Введите ваше имя"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email *</label>
                            <input
                                type="email"
                                name="client_email"
                                value={formData.client_email}
                                onChange={handleInputChange}
                                required
                                placeholder="your@email.com"
                            />
                        </div>

                        <div className="form-group">
                            <label>Телефон</label>
                            <input
                                type="tel"
                                name="client_phone"
                                value={formData.client_phone}
                                onChange={handleInputChange}
                                placeholder="+7 (999) 999-99-99"
                            />
                        </div>

                        {config?.code !== 'mag' && <div className="form-group">
                            <label>Удобный способ связи *</label>
                            <input
                                type="text"
                                name="preferred_contact"
                                value={formData.preferred_contact}
                                onChange={handleInputChange}
                                required
                                placeholder="Telegram, WhatsApp, звонок..."
                            />
                            <small className="form-hint">
                                Напишите, в каком мессенджере или как удобнее связаться
                            </small>
                        </div>}

                        <div className="form-group">
                            <label>Сообщение</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows="4"
                                placeholder="Опишите ваш запрос..."
                            />
                        </div>

                        <div className="form-footer">
                            <div className="booking-summary">
                                <div className="summary-item">
                                    <span>Запись на приём к {therapistName}</span>
                                    {selectedTopic && (
                                        <span className="selected-topic">Тема: {selectedTopic}</span>
                                    )}
                                </div>
                                {currentPrice && (
                                    <div className="summary-price">
                                        💰 {currentPrice} ₽
                                        {selectedTopic && topic_prices && topic_prices[selectedTopic] && (
                                            <small className="price-note">(цена для выбранной темы)</small>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="form-actions-bottom">
                                <button type="button" className="btn-cancel" onClick={onCancel}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn-submit" disabled={loading || !hasSchedule || !selectedSlot}>
                                    <Send size={18} />
                                    {loading ? 'Отправка...' : 'Записаться'}
                                </button>
                            </div>
                        </div>
                    </form>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default BookingWidget;

